import { Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { SectionTitle } from "@/components/ui-typography";
import { MockCaseMenu } from "./MockCaseMenu";
import { getSession, inviteLoginHref, useSession } from "./session";
import {
  INVITE_ACCEPT_READY,
  INVITE_ACCEPTED,
  INVITE_DECLINED,
  INVITE_EXPIRED,
  INVITE_MISSING,
  INVITE_REVOKED,
  MOCK_SESSION_EMAIL,
  PUBLIC_INVITE_WORKSPACE,
  acceptDesignatedInvite,
  acceptIncomingInvite,
  declineDesignatedInvite,
  declineIncomingInvite,
  designatedInviteView,
  getMockPlanAccess,
  incomingInviteMeta,
  incomingInviteTitle,
  incomingJoinedSentence,
  inviteJoinedSentence,
  inviteMismatchSentence,
  safeInviteReturn,
  useMockPlanAccess,
  type DesignatedInviteView,
} from "./mock-plan-access";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  bar: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: `0 ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    maxWidth: "420px",
    width: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  note: { color: tokens.colorNeutralForeground3 },
  actions: { display: "flex", gap: "8px" },
});

function closedCopy(view: DesignatedInviteView): string | null {
  if (view.kind === "missing") return INVITE_MISSING;
  if (view.kind === "expired") return INVITE_EXPIRED;
  if (view.kind === "revoked") return INVITE_REVOKED;
  if (view.kind === "declined") return INVITE_DECLINED;
  if (view.kind === "accepted") return INVITE_ACCEPTED;
  if (view.kind === "mismatch") return inviteMismatchSentence(view.current);
  return null;
}

/** 지정 초대 수락. 실패 문장은 서로 다르고, 워크스페이스 이름은 성공 뒤에만 나온다. */
export function InviteAcceptPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { token } = useParams();
  const id = token ?? "";
  const session = useSession();
  const [sessionEmail, setSessionEmail] = useState(() => getSession()?.email ?? "");
  const [done, setDone] = useState<string | null>(null);
  const mine = useMockPlanAccess().incoming.find((item) => item.id === id) ?? null;
  const view = mine
    ? { kind: "pending" as const, role: mine.role }
    : designatedInviteView(id, session ? sessionEmail || MOCK_SESSION_EMAIL : "");
  const back = safeInviteReturn(search.get("next"));

  useEffect(() => {
    if (session) setSessionEmail(session.email);
  }, [session]);

  useEffect(() => {
    if (back && back !== `/i/${id}`) navigate(back, { replace: true });
  }, [back, id, navigate]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const closed = done ?? (!session && view.kind === "mismatch" ? null : closedCopy(view));

  return (
    <div className={styles.page}>
      <header className={styles.bar}>
        <MockCaseMenu
          cases={[
            { id: "match", label: "초대 이메일" },
            { id: "other", label: "다른 이메일" },
          ]}
          onPick={(pick) => {
            setDone(null);
            const invite = getMockPlanAccess().invites.find((item) => item.id === id);
            setSessionEmail(pick === "match" && invite ? invite.email : MOCK_SESSION_EMAIL);
          }}
        />
      </header>
      <main className={styles.main}>
        {closed ? (
          <GhostEmptyState title={closed} />
        ) : !session ? (
          <>
            <Caption1 className={styles.note}>로그인한 뒤에 수락합니다.</Caption1>
            <div>
              <FuiButton appearance="primary" density="dialog" onClick={() => navigate(inviteLoginHref(`/i/${id}`))}>
                로그인
              </FuiButton>
            </div>
          </>
        ) : view.kind === "pending" ? (
          <>
            {mine ? (
              <>
                <SectionTitle>{incomingInviteTitle(mine)}</SectionTitle>
                <Caption1 className={styles.note}>{incomingInviteMeta(mine)}</Caption1>
              </>
            ) : (
              <>
                <Caption1 className={styles.note}>{INVITE_ACCEPT_READY}</Caption1>
                <Caption1 className={styles.note}>{view.role}</Caption1>
              </>
            )}
            <div className={styles.actions}>
              <FuiButton
                appearance="outline"
                density="dialog"
                onClick={() => {
                  if (mine) declineIncomingInvite(mine.id);
                  else declineDesignatedInvite(id, sessionEmail);
                  setDone(INVITE_DECLINED);
                }}
              >
                거절
              </FuiButton>
              <FuiButton
                appearance="primary"
                density="dialog"
                onClick={() => {
                  if (mine) {
                    const sentence = incomingJoinedSentence(mine);
                    acceptIncomingInvite(mine.id);
                    setDone(sentence);
                    return;
                  }
                  acceptDesignatedInvite(id, sessionEmail);
                  setDone(inviteJoinedSentence(PUBLIC_INVITE_WORKSPACE, view.role));
                }}
              >
                수락
              </FuiButton>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
